/**
 * Current-location resolution for multi-location staff (PR A / #399).
 *
 * THE BUG THIS FIXES (found live in dev, 2026-07-23): `GET /business-locations` (no path
 * param) resolves the salon through the legacy `salon_staff.id = user_id`
 * join — a link that does NOT follow `staff_users.last_used_salon_id`. After
 * a location switch (PATCH /staff-users/me + reload), every consumer of
 * `getSalons()[0]` re-derived the OLD salon on every boot — including hard
 * refreshes — leaving the app in a mixed state: server-scoped fetches
 * (calendar) showed the new location while uuid-scoped fetches (staff,
 * services, clients, products, settings) carried the old salon uuid. Some
 * backends 403'd (active-location containment), others returned the old
 * location's data (membership-only checks), so the frontend must simply
 * never issue old-uuid requests.
 *
 * THE RULE: when the flag is ON, the current location is
 * `available_locations.find(l => l.salon_id === selected_salon_id)` from
 * `/access`, and its `salon_uuid` is the ONLY salon identity the app may use.
 * The full salon record is then fetched via `GET /business-locations/{salon_uuid}`.
 * That endpoint's legacy query can return extra records (it also matches the
 * `salon_staff` row), so `pickSalonRecord` selects the row whose `id` (the
 * API exposes `uuid AS id`) equals the wanted uuid.
 *
 * Flag OFF (`available_locations` empty) → legacy `getSalons()[0]`, unchanged.
 *
 * Identifiers stay `salon`-scoped verbatim (no rename); only UI copy says
 * "location".
 */
import type {
  AvailableLocation,
  FeatureAccessResponse,
} from "../types/featureAccess";

type AccessLike = Pick<
  FeatureAccessResponse,
  "available_locations" | "selected_salon_id"
> | null;

/**
 * The location the signed-in user is currently working in, per /access.
 * `null` when the flag is OFF, /access hasn't loaded, or no selection exists
 * yet (first sign-in — the picker gate handles that) → callers fall back to
 * the legacy single-location path.
 */
export function resolveCurrentLocation(
  access: AccessLike | undefined,
): AvailableLocation | null {
  const locations = access?.available_locations ?? [];
  const selectedSalonId = access?.selected_salon_id ?? null;
  if (locations.length === 0 || selectedSalonId == null) return null;
  return locations.find((loc) => loc.salon_id === selectedSalonId) ?? null;
}

/**
 * Select the salon record matching `salonUuid` from a GET /business-locations[/{uuid}]
 * response (object or array; the API exposes `uuid AS id`). Returns null when
 * no exact match — callers must treat that as "resolution failed" and fall
 * back explicitly rather than silently using another salon's record.
 */
export function pickSalonRecord(
  records: unknown,
  salonUuid: string,
): Record<string, any> | null {
  const list: any[] = Array.isArray(records)
    ? records
    : records
      ? [records]
      : [];
  return list.find((record) => record?.id === salonUuid) ?? null;
}

interface FetchCurrentSalonRecordDeps {
  access: AccessLike | undefined;
  getSalons: () => Promise<any>;
  getSalonByUuid: (salonUuid: string) => Promise<any>;
}

/**
 * Minimal salon record synthesized from an /access AvailableLocation. Used
 * ONLY when GET /business-locations/{uuid} fails to return the resolved location's
 * record (live gap: the backend's `get_salon` INNER JOINs legacy
 * `salon_staff`, so a support-created location with no legacy staff rows is
 * excluded from its own by-uuid response — the 200 array carries only the
 * caller's staff-joined salon). The synthesized record keeps `id` pinned to
 * the CURRENT location's uuid so every read and write stays on the right
 * salon; extra fields (times, subscription status, booking settings) are
 * simply absent until the backend join is fixed.
 */
export function salonRecordFromLocation(
  location: AvailableLocation,
): Record<string, any> {
  return {
    // Explicit provenance marker read by isDegradedSalonRecord — more robust
    // than inferring degradation from which keys happen to be absent.
    __accessDerived: true,
    id: location.salon_uuid,
    salon_name: location.name,
    street_address_1: location.street_address_1,
    city: location.city,
    state: location.state,
  };
}

/**
 * True when `record` is the minimal /access-derived salon synthesized by
 * salonRecordFromLocation rather than a full GET /business-locations row. Primary signal
 * is the explicit `__accessDerived` marker the synthesizer writes; the
 * key-absence heuristic (a real row always carries `status`, `email`, and
 * `timezone` keys, even when their values are null) is kept as a fallback for
 * records that predate the marker (e.g. persisted/cloned copies). Writers
 * MUST NOT submit a full-record form from a degraded record: the form
 * back-fills missing fields with ""/defaults and the editor's browser
 * timezone, which would overwrite real DB values.
 */
export function isDegradedSalonRecord(
  record: Record<string, any> | null | undefined,
): boolean {
  if (!record) return false;
  if (record.__accessDerived === true) return true;
  return (
    !("status" in record) && !("email" in record) && !("timezone" in record)
  );
}

/**
 * The one way to fetch "the salon record for the location the user is
 * currently working in". Every boot-time or refresh-time consumer that used
 * to do `getSalons()[0]` must go through this instead.
 *
 * INVARIANT: once /access resolves a current location, the returned record's
 * `id` is ALWAYS that location's uuid — never another salon's. The legacy
 * `getSalons()` fallback runs ONLY when no location is resolved (flag OFF /
 * unresolved); falling back to it post-resolution is exactly how the
 * settings page once rendered (and nearly wrote to) the WRONG salon after a
 * location switch.
 */
export async function fetchCurrentSalonRecord({
  access,
  getSalons,
  getSalonByUuid,
}: FetchCurrentSalonRecordDeps): Promise<Record<string, any> | null> {
  const currentLocation = resolveCurrentLocation(access);
  if (currentLocation) {
    let match: Record<string, any> | null = null;
    try {
      const records = await getSalonByUuid(currentLocation.salon_uuid);
      match = pickSalonRecord(records, currentLocation.salon_uuid);
    } catch (error) {
      console.error(
        "fetchCurrentSalonRecord: GET /business-locations/{uuid} failed for",
        currentLocation.salon_uuid,
        error,
      );
    }
    if (match) return match;
    // The by-uuid response did not contain the resolved location (legacy
    // salon_staff INNER JOIN gap — see salonRecordFromLocation). NEVER fall
    // back to the legacy staff-joined record: it is a DIFFERENT salon, and
    // rendering it leaks the other location's data into settings while
    // poisoning writes (businessInfo.id targets PUT /business-locations). Degrade to a
    // minimal record built from /access instead, identity intact.
    console.error(
      "fetchCurrentSalonRecord: GET /business-locations/{uuid} returned no record matching",
      currentLocation.salon_uuid,
      "— using minimal /access-derived record (backend salon_staff join gap)",
    );
    return salonRecordFromLocation(currentLocation);
  }
  const salons = await getSalons();
  const list: any[] = Array.isArray(salons) ? salons : salons ? [salons] : [];
  return list[0] ?? null;
}

/** One-line address for a location row: "214 Newbury St · Boston, MA". */
export function formatLocationAddress(loc: AvailableLocation): string {
  const cityState = [loc.city, loc.state].filter(Boolean).join(", ");
  return [loc.street_address_1, cityState].filter(Boolean).join(" · ");
}
